<script lang="ts">
  // Account administration (global administrators): create and invite, invite an account
  // imported from Mattermost, reset, deactivate, administrator flag. Links are shown here,
  // to be passed on by the administrator (no e-mail is sent in v1).
  import { onMount } from 'svelte';
  import { api, ApiError, type Account } from '../lib/api';
  import { dayAndTime } from '../lib/format';
  import { router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';

  let accounts = $state<Account[] | null>(null);
  let error = $state('');
  let filter = $state<'people' | 'waiting' | 'deactivated' | 'bots'>('people');

  /** The last link created, shown until dismissed. */
  let link = $state<{ url: string; who: string; kind: 'activate' | 'reset' } | null>(null);
  let copied = $state(false);

  const message = (e: unknown) => (e instanceof ApiError ? e.message : 'Une erreur est survenue');

  async function load() {
    try {
      accounts = await api.accounts();
    } catch (e) {
      error = message(e);
    }
  }
  onMount(load);

  const shown = $derived(
    (accounts ?? []).filter((a) => {
      if (filter === 'bots') return a.is_bot;
      if (a.is_bot) return false;
      if (filter === 'deactivated') return a.status === 'deactivated';
      if (filter === 'waiting') return a.status === 'invited' || a.status === 'not_invited' || a.status === 'reset';
      return a.status !== 'deactivated';
    }),
  );
  const counts = $derived({
    waiting: (accounts ?? []).filter((a) => !a.is_bot && ['invited', 'not_invited', 'reset'].includes(a.status)).length,
    deactivated: (accounts ?? []).filter((a) => !a.is_bot && a.status === 'deactivated').length,
    bots: (accounts ?? []).filter((a) => a.is_bot).length,
  });

  const STATUS: Record<Account['status'], string> = {
    active: 'Actif',
    invited: 'Invitation envoyée',
    reset: 'Réinitialisation en cours',
    not_invited: 'Jamais invité',
    deactivated: 'Désactivé',
  };

  function replace(account: Account) {
    if (accounts) accounts = accounts.map((a) => (a.id === account.id ? account : a));
  }

  // --- Actions ------------------------------------------------------------------------------
  let busy = $state<string | null>(null);
  let rowError = $state<{ id: string; text: string } | null>(null);
  /** The account whose invitation asks for an e-mail. */
  let inviting = $state<{ id: string; email: string } | null>(null);

  async function act(account: Account, run: () => Promise<void>) {
    busy = account.id;
    rowError = null;
    try {
      await run();
    } catch (e) {
      rowError = { id: account.id, text: message(e) };
    } finally {
      busy = null;
    }
  }

  const invite = (account: Account, email?: string) =>
    act(account, async () => {
      const r = await api.inviteAccount(account.id, email);
      replace(r.account);
      link = { url: r.link, who: r.account.display_name, kind: 'activate' };
      copied = false;
      inviting = null;
    });

  const reset = (account: Account) => {
    if (!confirm(`Réinitialiser le compte de ${account.display_name} ? Ses sessions sont fermées tout de suite ; il choisira un nouveau mot de passe et reconfigurera son code.`)) return;
    void act(account, async () => {
      const r = await api.resetAccount(account.id);
      replace(r.account);
      link = { url: r.link, who: r.account.display_name, kind: 'reset' };
      copied = false;
    });
  };

  const setDeactivated = (account: Account, deactivated: boolean) => {
    if (deactivated && !confirm(`Désactiver le compte de ${account.display_name} ? Ses sessions sont fermées ; ses messages restent.`)) return;
    void act(account, async () => replace(await api.updateAccount(account.id, { deactivated })));
  };

  const setAdmin = (account: Account, is_admin: boolean) =>
    act(account, async () => replace(await api.updateAccount(account.id, { is_admin })));

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      copied = true;
    } catch {
      copied = false;
    }
  }

  // --- New account --------------------------------------------------------------------------
  let creating = $state(false);
  let form = $state({ email: '', display_name: '', username: '', is_admin: false });
  let createError = $state('');
  async function create(event: SubmitEvent) {
    event.preventDefault();
    createError = '';
    try {
      const r = await api.createAccount({
        email: form.email,
        display_name: form.display_name,
        username: form.username.trim() || undefined,
        is_admin: form.is_admin,
      });
      accounts = [...(accounts ?? []), r.account];
      link = { url: r.link, who: r.account.display_name, kind: 'activate' };
      copied = false;
      form = { email: '', display_name: '', username: '', is_admin: false };
      creating = false;
    } catch (e) {
      createError = message(e);
    }
  }
</script>

<section class="page" aria-labelledby="admin-title">
  <header>
    <button type="button" class="back" aria-label="Retour aux conversations" onclick={() => router.go('/')}>
      <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
    </button>
    <div class="title">
      <h1 id="admin-title">Administration</h1>
      <p class="sub">Comptes de la messagerie</p>
    </div>
    <button type="button" class="btn primary" onclick={() => (creating = !creating)} aria-expanded={creating}>Nouveau compte</button>
  </header>

  <div class="column">
    {#if !app.me?.is_admin}
      <p class="error" role="alert">Cette page est réservée aux administrateurs.</p>
    {:else}
      {#if error}<p class="error" role="alert">{error}</p>{/if}

      {#if link}
        <section class="link-box" aria-labelledby="link-title" role="status">
          <h2 id="link-title">
            {link.kind === 'reset' ? 'Lien de réinitialisation' : "Lien d'activation"} pour {link.who}
          </h2>
          <p class="hint">
            Transmettez-le à la personne (il n'est pas envoyé par e-mail). Valable 72 heures, une seule fois.
            {link.kind === 'reset' ? 'Elle choisira un nouveau mot de passe et reconfigurera son code.' : 'Elle choisira son mot de passe et configurera son code à usage unique.'}
          </p>
          <div class="link-row">
            <input class="input" readonly value={link.url} aria-label="Lien à transmettre" onfocus={(e) => e.currentTarget.select()} />
            <button type="button" class="btn primary" onclick={copy}>{copied ? 'Copié' : 'Copier'}</button>
            <button type="button" class="btn" onclick={() => (link = null)}>Fermer</button>
          </div>
        </section>
      {/if}

      {#if creating}
        <form class="card new" onsubmit={create} aria-label="Nouveau compte">
          <h2>Nouveau compte</h2>
          <p class="hint">Pour quelqu'un qui avait un compte Mattermost, utilisez plutôt « Inviter » sur son compte importé : il retrouvera son historique.</p>
          <div class="grid">
            <label class="field">E-mail
              <input class="input" type="email" required bind:value={form.email} autocomplete="off" />
            </label>
            <label class="field">Nom affiché
              <input class="input" required maxlength="64" bind:value={form.display_name} autocomplete="off" />
            </label>
            <label class="field">Nom d'utilisateur (facultatif)
              <input class="input" bind:value={form.username} placeholder="d'après l'e-mail" pattern="[a-z0-9._\-]+" autocomplete="off" />
            </label>
            <label class="check"><input type="checkbox" bind:checked={form.is_admin} /> Administrateur</label>
          </div>
          {#if createError}<p class="error" role="alert">{createError}</p>{/if}
          <div class="row">
            <button type="submit" class="btn primary">Créer et obtenir le lien</button>
            <button type="button" class="btn" onclick={() => (creating = false)}>Annuler</button>
          </div>
        </form>
      {/if}

      <div class="filters" role="tablist" aria-label="Comptes affichés">
        {#each [['people', 'Comptes', null], ['waiting', 'À activer', counts.waiting], ['deactivated', 'Désactivés', counts.deactivated], ['bots', 'Bots', counts.bots]] as [value, label, n] (value)}
          <button type="button" role="tab" aria-selected={filter === value} onclick={() => (filter = value as typeof filter)}>
            {label}{#if n}<span class="n">{n}</span>{/if}
          </button>
        {/each}
      </div>

      {#if !accounts}
        <p class="hint">Chargement…</p>
      {:else}
        <ul class="accounts">
          {#each shown as a (a.id)}
            <li class="account" aria-busy={busy === a.id}>
              <Avatar user={a} size={36} />
              <div class="who">
                <span class="name">
                  {a.display_name}
                  {#if a.is_admin}<span class="tag admin">Admin</span>{/if}
                  {#if a.imported}<span class="tag">Mattermost</span>{/if}
                  {#if a.id === app.me?.id}<span class="tag">Vous</span>{/if}
                </span>
                <span class="meta">@{a.username}{a.email ? ` · ${a.email}` : ''}</span>
                <span class="meta">
                  <span class="status {a.status}">{STATUS[a.status]}</span>
                  {#if a.link_expires_at} · lien valable jusqu'au {dayAndTime(a.link_expires_at)}{/if}
                  {#if a.last_seen_at} · vu {dayAndTime(a.last_seen_at)}{/if}
                </span>
                {#if inviting?.id === a.id}
                  <form
                    class="invite-form"
                    onsubmit={(e) => {
                      e.preventDefault();
                      void invite(a, inviting!.email);
                    }}
                  >
                    <label class="sr-only" for="invite-{a.id}">E-mail de {a.display_name}</label>
                    <input id="invite-{a.id}" class="input" type="email" required placeholder="adresse e-mail" bind:value={inviting.email} />
                    <button type="submit" class="btn primary">Inviter</button>
                    <button type="button" class="btn" onclick={() => (inviting = null)}>Annuler</button>
                  </form>
                {/if}
                {#if rowError?.id === a.id}<p class="error" role="alert">{rowError.text}</p>{/if}
              </div>
              {#if a.id !== app.me?.id}
                <div class="actions">
                  {#if !a.is_bot && (a.status === 'not_invited' || a.status === 'invited')}
                    <button
                      type="button"
                      class="btn"
                      disabled={busy === a.id}
                      onclick={() => (a.email ? invite(a) : (inviting = { id: a.id, email: '' }))}
                    >
                      {a.status === 'invited' ? 'Nouveau lien' : 'Inviter'}
                    </button>
                    {#if a.email && a.status === 'not_invited'}
                      <button type="button" class="link-btn" onclick={() => (inviting = { id: a.id, email: a.email ?? '' })}>Changer l'e-mail</button>
                    {/if}
                  {/if}
                  {#if a.status === 'active' || a.status === 'reset'}
                    <button type="button" class="btn" disabled={busy === a.id} onclick={() => reset(a)}>Réinitialiser</button>
                  {/if}
                  {#if !a.is_bot && a.status !== 'deactivated'}
                    <button type="button" class="link-btn" disabled={busy === a.id} onclick={() => setAdmin(a, !a.is_admin)}>
                      {a.is_admin ? 'Retirer admin' : 'Rendre admin'}
                    </button>
                  {/if}
                  {#if a.status === 'deactivated'}
                    <button type="button" class="btn" disabled={busy === a.id} onclick={() => setDeactivated(a, false)}>Réactiver</button>
                  {:else}
                    <button type="button" class="link-btn danger" disabled={busy === a.id} onclick={() => setDeactivated(a, true)}>Désactiver</button>
                  {/if}
                </div>
              {/if}
            </li>
          {:else}
            <li class="hint empty">Aucun compte dans cette liste.</li>
          {/each}
        </ul>
      {/if}
    {/if}
  </div>
</section>

<style>
  .page {
    height: 100%;
    overflow-y: auto;
    background: var(--bg-page);
  }
  header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: var(--header-h);
    padding: 8px 24px;
    border-bottom: 1px solid var(--border);
    background: var(--bg);
  }
  .title {
    flex: 1;
  }
  h1 {
    margin: 0;
    font-size: 18px;
    line-height: 26px;
  }
  .sub {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .back {
    display: none;
    width: 44px;
    height: 44px;
    margin-left: -12px;
    border: 0;
    background: transparent;
    color: var(--accent-text);
    align-items: center;
    justify-content: center;
  }
  .column {
    max-width: 920px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  h2 {
    margin: 0;
    font-size: 15px;
  }
  .hint {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .card,
  .link-box {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .link-box {
    border-color: var(--accent-border);
    background: var(--accent-soft);
  }
  .link-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .link-row .input {
    flex: 1 1 320px;
    font-family: var(--font-mono);
    font-size: 13px;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px 16px;
    align-items: end;
  }
  .check {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
  }
  .row {
    display: flex;
    gap: 8px;
  }
  .filters {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .filters button {
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: 16px;
    background: var(--bg);
    color: var(--ink-2);
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .filters button[aria-selected='true'] {
    background: var(--accent-soft);
    border-color: var(--accent-border);
    color: var(--accent-on-soft);
    font-weight: 600;
  }
  .n {
    font-size: 11px;
    font-weight: 700;
    color: var(--muted);
  }
  .accounts {
    list-style: none;
    margin: 0;
    padding: 0;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
  }
  .account {
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    border-top: 1px solid var(--border);
    align-items: flex-start;
  }
  .account:first-child {
    border-top: 0;
  }
  .account[aria-busy='true'] {
    opacity: 0.6;
  }
  .who {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .name {
    font-weight: 600;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  .meta {
    font-size: 12px;
    color: var(--muted);
    overflow-wrap: anywhere;
  }
  .tag {
    font-size: 11px;
    font-weight: 600;
    padding: 0 6px;
    border-radius: 8px;
    background: var(--bg-page);
    color: var(--ink-2);
    border: 1px solid var(--border);
  }
  .tag.admin {
    background: var(--accent-soft);
    color: var(--accent-on-soft);
    border-color: var(--accent-border);
  }
  .status {
    font-weight: 600;
  }
  .status.active {
    color: var(--status-done);
  }
  .status.invited,
  .status.reset {
    color: var(--status-doing-text);
  }
  .status.not_invited {
    color: var(--ink-2);
  }
  .status.deactivated {
    color: var(--danger);
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    align-items: center;
    justify-content: flex-end;
    max-width: 50%;
  }
  .actions .btn {
    height: 32px;
    font-size: 13px;
  }
  .link-btn {
    border: 0;
    background: none;
    padding: 0;
    color: var(--accent-text);
    font-size: 13px;
    font-weight: 600;
  }
  .link-btn.danger {
    color: var(--danger);
  }
  .invite-form {
    display: flex;
    gap: 6px;
    margin-top: 6px;
    flex-wrap: wrap;
  }
  .invite-form .input {
    flex: 1 1 200px;
    height: 34px;
  }
  .empty {
    padding: 16px;
  }
  @media (max-width: 759px) {
    header {
      padding: 8px 12px;
    }
    .back {
      display: flex;
    }
    .column {
      padding: 12px;
    }
    .account {
      flex-wrap: wrap;
    }
    .actions {
      max-width: none;
      width: 100%;
      justify-content: flex-start;
    }
  }
</style>
